import { useEffect, useState, FormEvent } from 'react';
import type { Brand } from '@lr/shared';
import { apiFetch } from '../../lib/api';

interface BrandRow extends Brand {
  items?: { count: number }[] | null;
}

export function AdminBrands() {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  function load() {
    apiFetch<BrandRow[]>('/api/admin/brands')
      .then(setBrands)
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Brands</h1>
          <div className="sub">{brands.length} {brands.length === 1 ? 'brand' : 'brands'}.</div>
        </div>
        <div className="actions">
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Add brand
          </button>
        </div>
      </div>

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : brands.length === 0 ? (
          <div className="a-empty">
            <div className="h">No brands yet</div>
            <div className="p">Add a brand so you can attach pieces to it.</div>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Add brand
            </button>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th style={{ width: 100 }}>Items</th>
                  <th style={{ width: 100 }}>Active</th>
                  <th style={{ width: 140 }}>Created</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => {
                  const count = b.items?.[0]?.count ?? 0;
                  return (
                    <tr key={b.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{b.name}</div>
                      </td>
                      <td className="id">{b.slug}</td>
                      <td className="num">{count}</td>
                      <td>
                        {b.is_active ? (
                          <span className="badge badge-good">Active</span>
                        ) : (
                          <span className="badge badge-neutral">Hidden</span>
                        )}
                      </td>
                      <td>{new Date(b.created_at).toLocaleDateString('en-GB')}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-ghost"
                          style={{ color: count > 0 ? 'var(--a-muted)' : 'var(--a-danger)' }}
                          title={
                            count > 0
                              ? `Has ${count} item${count === 1 ? '' : 's'} — reassign first`
                              : 'Delete'
                          }
                          onClick={async () => {
                            if (count > 0) return;
                            if (!confirm(`Delete brand "${b.name}"?`)) return;
                            try {
                              await apiFetch(`/api/admin/brands/${b.id}`, { method: 'DELETE' });
                              load();
                            } catch (e) {
                              alert(e instanceof Error ? e.message : 'Delete failed');
                            }
                          }}
                          disabled={count > 0}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateBrandModal
          onClose={(created) => {
            setShowCreate(false);
            if (created) load();
          }}
        />
      )}
    </>
  );
}

function CreateBrandModal({ onClose }: { onClose: (created?: boolean) => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const finalSlug = slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      await apiFetch('/api/admin/brands', {
        method: 'POST',
        body: JSON.stringify({ name, slug: finalSlug })
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <form onSubmit={onSubmit} style={{ display: 'contents' }}>
          <div className="a-modal-head">
            <h2>New brand</h2>
            <button type="button" className="btn-ghost" onClick={() => onClose()}>
              ×
            </button>
          </div>
          <div className="a-modal-body">
            {error && <div className="a-alert a-alert-error">{error}</div>}
            <div className="a-form">
              <div className="a-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Stone Island"
                  required
                />
              </div>
              <div className="a-field">
                <label>Slug</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="auto-generated"
                />
                <div className="help">URL-friendly version. Lowercase letters, numbers, dashes.</div>
              </div>
            </div>
          </div>
          <div className="a-modal-foot">
            <button type="button" className="btn-secondary" onClick={() => onClose()}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create brand'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
